import React from 'react';
import { Scale, Printer, ArrowLeft, ShieldCheck, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  const lastUpdated = "February 12, 2026";

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 shadow-xl rounded-[2.5rem] overflow-hidden border border-slate-200 dark:border-slate-800">
        
        {/* Header */}
        <div className="bg-brand px-8 py-8 flex justify-between items-center text-white">
          <div className="flex items-center gap-3">
            <Scale className="w-8 h-8 opacity-90" />
            <div>
              <h1 className="text-2xl font-black tracking-tight">Terms of Protocol</h1>
              <p className="text-2xs font-bold uppercase tracking-widest opacity-70">EverydayOS Service Agreement</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors sm:hidden"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={handlePrint}
              className="hidden sm:flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-widest py-2.5 px-5 rounded-xl transition shadow-sm backdrop-blur-md"
            >
              <Printer className="w-4 h-4" /> Export PDF
            </button>
          </div>
        </div>

        {/* Content Container */}
        <div className="p-10 prose prose-slate dark:prose-invert max-w-none text-slate-600 dark:text-slate-400 leading-relaxed">
          <p className="text-xs font-black text-slate-400 uppercase tracking-tighter mb-10 border-b border-slate-100 dark:border-slate-800 pb-4">
            Last Protocol Revision: {lastUpdated}
          </p>

          <section className="mb-10">
            <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="text-brand">01.</span> Acceptance of Terms
            </h2>
            <p className="font-medium">
              By accessing or using <strong>EverydayOS</strong> (the "Service"), provided by <strong>N/A Management Services LTD</strong> ("we," "us," or "our"), a company registered in England and Wales, you agree to be bound by these Terms of Service. If you do not agree to these protocols, you must cease use of the Service immediately.
            </p>
          </section>

          <section className="mb-10 bg-indigo-50 dark:bg-indigo-900/10 p-8 rounded-[2rem] border border-indigo-100 dark:border-indigo-900/30">
            <h2 className="text-xl font-black text-indigo-900 dark:text-indigo-300 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> Consumer Standards (UK/EEA)
            </h2>
            <p className="text-sm font-bold text-indigo-800 dark:text-indigo-400 mb-4">
              In accordance with the <strong>UK Consumer Rights Act 2015</strong>, we warrant that the Service and any digital content provided will be:
            </p>
            <ul className="space-y-3 ml-2">
              {[
                "Of satisfactory quality and standard",
                "Fit for its intended household purpose",
                "As described in EverydayOS technical specifications"
              ].map((item, i) => (
                <li key={i} className="flex gap-3 items-center text-sm font-medium">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
               <span className="text-brand">02.</span> Identity & Account Access
            </h2>
            <ul className="space-y-4 list-none p-0">
              <li className="flex gap-4">
                <span className="font-black text-slate-300 dark:text-slate-700">A.</span>
                <p className="text-sm">You must meet the minimum age protocol (16+ in UK/EEA) to initialize a personal node.</p>
              </li>
              <li className="flex gap-4">
                <span className="font-black text-slate-300 dark:text-slate-700">B.</span>
                <p className="text-sm">Account security and all activities occurring within your household registry are your sole responsibility.</p>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
               <span className="text-brand">03.</span> Subscriptions & Financial Handshake
            </h2>
            <div className="grid gap-6">
              <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-2">Cooling-off Period</p>
                <p className="text-xs">Under Consumer Contracts Regulations, you have a 14-day right to cancel. By accessing digital content immediately upon purchase, you acknowledge the waiver of this right for immediate service delivery.</p>
              </div>
              <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-2">Stripe Integration</p>
                <p className="text-xs">All financial transactions are handled by Stripe. EverydayOS does not store or process raw credit card data on its own internal nodes.</p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
               <span className="text-brand">04.</span> Integration & Liability
            </h2>
            <p className="mb-4">
              <strong>Third-Party Nodes:</strong> EverydayOS allows synchronization with Google Calendar. We are an independent entity from Google LLC.
            </p>
            <div className="p-5 border-l-4 border-indigo-200 dark:border-indigo-900 italic text-sm">
              "To the extent permitted by the laws of England and Wales, our total liability shall not exceed the total amount paid by you for the Service in the 12 months preceding any claim."
            </div>
          </section>

          {/* Footer Contact */}
          <div className="mt-20 pt-10 border-t border-slate-100 dark:border-slate-800 text-center">
            <div className="flex justify-center mb-6">
               <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-400">
                  <FileText className="w-6 h-6" />
               </div>
            </div>
            <p className="font-black text-slate-900 dark:text-white uppercase tracking-widest text-sm mb-2">N/A Management Services LTD</p>
            <p className="text-xs font-bold text-slate-400">Company Registered in England & Wales</p>
            <p className="text-xs font-bold mt-4 text-brand">
              Legal: nathanielaniballi@gmail.com
            </p>
            <div className="mt-8 flex justify-center items-center gap-2 opacity-50 grayscale">
               <div className="w-2 h-2 rounded-full bg-emerald-500" />
               <p className="text-2xs font-black uppercase tracking-[0.4em]">EverydayOS Secure Terminal</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;